import React from 'react';
import i18n from '@/i18n';

const CHUNK_RE = /ChunkLoadError|Loading chunk|Loading CSS chunk|Failed to fetch dynamically imported module|error loading dynamically imported module/i;
const RELOAD_KEY = 'becaim-chunk-reload-at';

/** True for the "app was updated while this tab was open" failure mode. */
export const isStaleBuildError = (err) =>
  !!err && (err.name === 'ChunkLoadError' || CHUNK_RE.test(String(err.message || err)));

/** Reload at most once per minute so a genuinely broken deploy can't loop. */
export function recoverFromStaleBuild() {
  const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
  if (Date.now() - last < 60_000) return false;
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  window.location.reload();
  return true;
}

/**
 * Root error boundary. Without this, any render error (most often a missing
 * code-split chunk after a deploy) leaves a blank white page.
 */
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, recovering: false };
  }

  static getDerivedStateFromError(error) {
    return { error, recovering: isStaleBuildError(error) };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(i18n.t('c.AppErrorBoundary.app_crashed'), error, info?.componentStack);
    if (isStaleBuildError(error) && !recoverFromStaleBuild()) {
      this.setState({ recovering: false });
    }
  }

  render() {
    const { error, recovering } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50"
        data-testid="app-error-boundary">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">
            {recovering ? i18n.t('c.AppErrorBoundary.updating') : i18n.t('c.AppErrorBoundary.something_went_wrong')}
          </div>
          <h1 className="text-xl font-bold text-slate-900 mt-1">
            {recovering ? i18n.t('c.AppErrorBoundary.a_new_version_is_loading') : i18n.t('c.AppErrorBoundary.this_page_hit_an_unexpected_error')}
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            {recovering
              ? i18n.t('c.AppErrorBoundary.the_app_was_updated_while_this')
              : i18n.t('c.AppErrorBoundary.your_data_is_safe_reload_to')}
          </p>
          <pre className="mt-3 text-[11px] text-slate-400 whitespace-pre-wrap break-words max-h-24 overflow-auto">
            {String(error.message || error)}
          </pre>
          <div className="mt-4 flex gap-2">
            <button onClick={() => window.location.reload()} data-testid="app-error-reload"
              className="px-4 py-2 rounded-lg bg-slate-950 text-white text-sm font-bold"> {i18n.t('c.AppErrorBoundary.reload')} </button>
            <button onClick={() => { window.location.href = '/business/app'; }}
              data-testid="app-error-home"
              className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700"> {i18n.t('c.AppErrorBoundary.back_to_dashboard')} </button>
          </div>
        </div>
      </div>
    );
  }
}
